import { P as PubSub } from './pubsub-CoPYzvVm.js';
import './notyf-DIw0RvBj.js';

/**
 * @class ModalTimer
 * @extends {HTMLElement}
 * @description Controls auto-open timing and acceptance storage for a linked modal.
 * Waits for `window` `load`, skips auto-open when already accepted, and wires
 * accept-button persistence via localStorage. Test mode always auto-opens;
 * section reload re-open is editor-only.
 *
 * Expected data attributes on the element:
 * - `data-modal-id` — id of the target `modal-element`
 * - `data-section-id` — Shopify section id (test mode section:load matching)
 * - `data-open-timing` — `immediate`, `delayed`, or `test`
 * - `data-auto-open-delay` — delay in ms before opening (delayed mode only; defaults to 3000)
 * - `data-storage-key` — localStorage key for acceptance; persistence and auto-open
 *   (immediate/delayed) are skipped when absent
 */
class ModalTimer extends HTMLElement {
  /** @type {number | undefined} */
  #openTimeout;

  /** @type {AbortController | null} */
  #acceptController = null;

  static #defaultDelayMs = 3000;

  /**
   * @method connectedCallback
   * @description Waits for page load, then schedules auto-open and wires acceptance.
   */
  connectedCallback() {
    if (this.dataset.openTiming === 'test' && this.#isDesignMode()) {
      document.addEventListener('shopify:section:load', this.#onSectionLoad);
      this.#openModal();
      this.#handleModalState();
      return;
    }

    if (document.readyState === 'complete') {
      this.#init();
      return;
    }

    window.addEventListener('load', () => this.#init(), { once: true });
  }

  /**
   * @method disconnectedCallback
   * @description Clears any pending auto-open timeout and removes section:load listener.
   */
  disconnectedCallback() {
    this.#clearOpenTimeout();
    this.#removeStaleModal();
    this.#acceptController?.abort();
    this.#acceptController = null;
    document.removeEventListener('shopify:section:load', this.#onSectionLoad);
  }

  /**
   * @method #init
   * @description Schedules auto-open and wires acceptance for immediate/delayed modes.
   */
  #init() {
    if (!this.dataset.storageKey) return;
    if (this.#isAlreadyAccepted()) return;
    this.#scheduleModalAction();
    this.#handleModalState();
  }

  /**
   * @method #onSectionLoad
   * @description Re-opens the modal after section reload in the theme editor.
   * @param {{ detail?: { sectionId?: string } }} event
   */
  #onSectionLoad = ({ detail }) => {
    if (detail?.sectionId !== this.dataset.sectionId) return;

    this.#removeStaleModal();
    this.#openModal();
    this.#handleModalState();
  };

  /**
   * @method #isDesignMode
   * @description Returns true when the Shopify theme editor is active.
   * @returns {boolean}
   */
  #isDesignMode() {
    return typeof Shopify !== 'undefined' && Boolean(Shopify.designMode);
  }

  /**
   * @method #isAlreadyAccepted
   * @description Returns true when the user has already accepted this modal.
   * @returns {boolean}
   */
  #isAlreadyAccepted() {
    const storageKey = this.dataset.storageKey;
    if (!storageKey) return false;
    if (localStorage.getItem(storageKey) === 'accepted') return true;
    return false;
  }

  /**
   * @method #handleModalState
   * @description Stores acceptance in localStorage when the accept button is clicked.
   */
  #handleModalState() {
    const modal = this.#getModal();
    const acceptButton = modal?.querySelector('[data-modal-accept]');
    const storageKey = this.dataset.storageKey;
    if (!acceptButton || !storageKey) return;

    this.#acceptController?.abort();
    this.#acceptController = new AbortController();

    acceptButton.addEventListener(
      'click',
      () => {
        localStorage.setItem(storageKey, 'accepted');
      },
      { signal: this.#acceptController.signal }
    );
  }

  /**
   * @method #removeStaleModal
   * @description Removes teleported modal elements left in document.body after section reload.
   */
  #removeStaleModal() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    document.querySelectorAll(`modal-element[data-section-id="${sectionId}"]`).forEach((modal) => {
      if (modal.parentElement === document.body) modal.remove();
    });
  }

  /**
   * @method #scheduleModalAction
   * @description Schedules immediate or delayed auto-open based on `data-open-timing`.
   */
  #scheduleModalAction() {
    const openTiming = this.dataset.openTiming || 'immediate';

    if (openTiming !== 'immediate' && openTiming !== 'delayed') return;
    this.#clearOpenTimeout();

    if (openTiming === 'immediate') {
      this.#openModal();
      return;
    }

    const delay = Number(this.dataset.autoOpenDelay) || ModalTimer.#defaultDelayMs;
    this.#openTimeout = window.setTimeout(() => this.#openModal(), delay);
  }

  /**
   * @method #openModal
   * @description Publishes `dialog:open` via PubSub to open the linked modal.
   */
  #openModal() {
    const modalId = this.dataset.modalId;
    console.log('open modal:', modalId);
    if (!modalId) return;

    PubSub.publish('dialog:open', { id: modalId });
  }

  /**
   * @method #clearOpenTimeout
   * @description Clears a pending auto-open timeout.
   */
  #clearOpenTimeout() {
    if (!this.#openTimeout) return;
    window.clearTimeout(this.#openTimeout);
    this.#openTimeout = undefined;
  }

  /**
   * @method #getModal
   * @description Returns the linked modal element by `data-modal-id`.
   * @returns {HTMLElement | null}
   */
  #getModal() {
    const modalId = this.dataset.modalId;
    if (!modalId) return null;
    return document.getElementById(modalId);
  }
}

customElements.define('modal-timer', ModalTimer);
